import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { clearAdminSession } from "../lib/api";

const NAV_LINKS = [
  { to: "/admin/branding", label: "branding" },
  { to: "/admin/header-modules", label: "header-modules" },
  { to: "/admin/backup", label: "backup" },
  { to: "/admin/newsletter", label: "mailing-list" },
  { to: "/admin/moderation", label: "moderation" },
];

/**
 * Retro BIOS command bar for admin pages only. Replaces per-page chip clusters.
 * Row 1: ADMIN | links | +post sign-out. Mobile: ADMIN Menu with a backdrop list.
 */
export default function AdminChrome({ title }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef(null);
  const menuPanelRef = useRef(null);
  const onDashboard = location.pathname === "/admin";

  const signOut = () => {
    clearAdminSession();
    setMenuOpen(false);
    navigate("/admin/login");
  };

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") { setMenuOpen(false); menuButtonRef.current?.focus(); }
      if (e.key === "Tab" && menuPanelRef.current) {
        const items = Array.from(menuPanelRef.current.querySelectorAll("a, button"));
        if (!items.length) return;
        const first = items[0]; const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { last.focus(); e.preventDefault(); }
        else if (!e.shiftKey && document.activeElement === last) { first.focus(); e.preventDefault(); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const linkClass = (path) => `xs-admin-nav-link ${location.pathname.startsWith(path) ? "active" : ""}`;

  const mobileItems = [
    ...NAV_LINKS,
    { to: "/admin/new", label: "+ post" },
  ];

  return (
    <>
      <a href="#admin-content" className="xs-skip-link">Skip to content</a>
      <div className="xs-admin-chrome">
        <div className="xs-admin-bar" role="navigation" aria-label="Admin navigation">
          <Link to="/admin" className={`xs-admin-brand ${onDashboard ? "active" : ""}`}>ADMIN</Link>
          <span className="xs-admin-sep" aria-hidden="true">|</span>
          <nav className="xs-admin-nav-desktop" aria-label="Admin tools">
            {NAV_LINKS.map((link) => <Link key={link.to} to={link.to} className={linkClass(link.to)}>{link.label}</Link>)}
          </nav>
          <span className="xs-admin-sep xs-admin-sep-desktop" aria-hidden="true">|</span>
          <div className="xs-admin-nav-actions">
            <Link to="/admin/new" className={`xs-admin-nav-link ${location.pathname.startsWith("/admin/new") || location.pathname.startsWith("/admin/edit") ? "active" : ""}`}>+ post</Link>
            <button type="button" className="xs-admin-nav-button" onClick={signOut} data-testid="admin-sign-out">sign-out</button>
          </div>
          <button
            type="button"
            className="xs-admin-menu-button"
            ref={menuButtonRef}
            aria-expanded={menuOpen}
            aria-haspopup="true"
            onClick={() => setMenuOpen((open) => !open)}
          >
            Menu
          </button>
        </div>
      </div>

      {menuOpen && (
        <>
          <div className="xs-admin-menu-backdrop" onClick={() => { setMenuOpen(false); menuButtonRef.current?.focus(); }} />
          <div className="xs-admin-menu-panel" ref={menuPanelRef} role="dialog" aria-modal="true" aria-label="Admin menu">
            {mobileItems.map((link) => (
              <Link key={link.to} to={link.to} className={`xs-admin-menu-item ${location.pathname.startsWith(link.to) ? "active" : ""}`}>{link.label}</Link>
            ))}
            <button type="button" className="xs-admin-menu-item xs-admin-menu-signout" onClick={signOut}>sign-out</button>
          </div>
        </>
      )}
    </>
  );
}
